import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";

import { useCreateCategorizedDataset, useGetDefaultDatasetCategory } from "api/api-hooks/datasets-hooks";

import { DatasetCategoryDropdown } from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import { DATASET_ACCESS_TYPES, DATASET_DATA_TYPES } from "../CreateDatasetDialog";

interface ICreateMultipleDatasetsProps {
    setCreateDialogVisibility: (value: boolean) => void;
}

interface ICommonDatasetProps {
    id: string;
    title: string;
    categoryId: number | undefined;
    dataType: string;
    accessType: string;
    unit: string;
}

const CreateMultipleDatasets = ({setCreateDialogVisibility}:ICreateMultipleDatasetsProps) => {
    const [defaultCategory, setDefaultCategory] = useState<number | undefined>(undefined);
    const [datasetsToCreate, setDatasetsToCreate] = useState<any[]>([]);
    const [createdDatasets, setCreatedDatasets] = useState<Partial<BaseDatasetDTO[]>>([]);

    const [advancedDialogVisibility, setAdvancedDialogVisibility] = useState(false);
    const [prefixes, setPrefixes] = useState<string[]>([]);
    const [suffixes, setSuffixes] = useState<string[]>([]);
    const [datasetsCopy, setDatasetsCopy] = useState<any[]>([]);
    const [advancedUsed, setAdvancedUsed] = useState(false);

    const defaultCategoryQuery = useGetDefaultDatasetCategory();

    useEffect(() => {
        if (defaultCategoryQuery.isFetched) {
            setDefaultCategory(defaultCategoryQuery.data?.id);
        }
        if (defaultCategoryQuery.isError) {
            easasAlert({ summary: "Error fetching category" });
        }
    }, [defaultCategoryQuery.data]);

    const generateUniqueId = (): string => {
        const timestamp = new Date().getTime().toString(16);
        const randomPart = Math.floor(Math.random() * 1000).toString(16);
        return `${timestamp}${randomPart}`;
    };

    const header = (
        <div className="flex gap-2 justify-content-between">
            <div className="flex gap-2">
                <DatasetCategoryDropdown value={defaultCategory} onChange={(e) => setDefaultCategory(e)}/>
                <Button
                    label="Advanced"
                    className="p-button-secondary"
                    onClick={() => setAdvancedDialogVisibility(true)}
                />
                <Dialog
                    onHide={() => setAdvancedDialogVisibility(false)}
                    visible={advancedDialogVisibility}
                    header="Advanced Menu"
                    style={{maxWidth: '300px', width: '300px'}}
                >
                    <div className="flex flex-column gap-2">
                        <p className="mb-0">Add prefixes (press ENTER to add)</p>
                        <Chips
                            className="p-fluid"
                            placeholder="Prefix"
                            value={prefixes}
                            onChange={(e) => setPrefixes(e.value)}
                        />
                        <p className="mb-0">Add suffixes (press ENTER to add)</p>
                        <Chips
                            className="p-fluid"
                            placeholder="Suffix"
                            value={suffixes}
                            onChange={(e) => setSuffixes(e.value)}
                        />
                        <Button label="Generate" className="p-button-success"
                                onClick={() => generateDatasetList()}/>
                    </div>
                </Dialog>
            </div>
            <div className="buttons flex gap-2">
                <Button
                    icon="pi pi-plus"
                    className="p-button-success p-mr-2"
                    onClick={() => addDataset()}
                />
                {advancedUsed &&
                    <Button
                        label="Reset"
                        className="p-button-secondary"
                        onClick={() => {
                            setDatasetsToCreate(datasetsCopy)
                            setAdvancedUsed(false)
                        }}/>}
            </div>
        </div>
    );

    const addDataset = () => {
        setDatasetsToCreate([
            ...datasetsToCreate,
            {
                id: generateUniqueId(),
                title: "",
                categoryId: defaultCategory,
                dataType: "ARBITRARY",
                accessType: "NORMAL",
                unit: "",
            },
        ])
    }

    const generateDatasetList = () => {
        const updatedDatasets = advancedUsed ? datasetsCopy : datasetsToCreate;
        const generatedDatasetList: ICommonDatasetProps[] = [];
        if (prefixes.length === 0 && suffixes.length === 0) {
            easasAlert({ summary: "Validation Error", detail: "Nothing to add", severity: "warn" });
            return;
        }

        if (updatedDatasets.length === 0) {
            easasAlert({ summary: "Validation Error", detail: "No datasets to create", severity: "warn" });
            return;
        }

        if (!advancedUsed) {
            setDatasetsCopy(updatedDatasets);
        }

        // Handle the case where only prefixes exist
        if (prefixes.length > 0 && suffixes.length === 0) {
            prefixes.map((prefix) => {
                updatedDatasets.map((dataset) => {
                    generatedDatasetList.push({
                        id: generateUniqueId(),
                        title: `${prefix  } ${  dataset.title}`,
                        categoryId: defaultCategory,
                        dataType: dataset.dataType,
                        accessType: dataset.accessType,
                        unit: dataset.unit,
                    });
                });
            });
        }

        // Handle the case where only suffixes exist
        if (suffixes.length > 0 && prefixes.length === 0) {
            updatedDatasets.map((dataset) => {
                suffixes.map((suffix) => {
                    generatedDatasetList.push({
                        id: generateUniqueId(),
                        title: `${dataset.title  } ${  suffix}`,
                        categoryId: defaultCategory,
                        dataType: dataset.dataType,
                        accessType: dataset.accessType,
                        unit: dataset.unit,
                    });
                });
            });
        }

        // Handle the case where both prefixes and suffixes exist
        if (prefixes.length > 0 && suffixes.length > 0) {
            prefixes.map((prefix) => {
                updatedDatasets.map((dataset) => {
                    suffixes.map((suffix) => {
                        generatedDatasetList.push({
                            id: generateUniqueId(),
                            title: `${prefix  } ${  dataset.title  } ${  suffix}`,
                            categoryId: defaultCategory,
                            dataType: dataset.dataType,
                            accessType: dataset.accessType,
                            unit: dataset.unit,
                        });
                    });
                });
            });
        }

        setDatasetsToCreate(generatedDatasetList);
        setAdvancedDialogVisibility(false);
        setAdvancedUsed(true);
    }


    const titleBodyTemplate = (options:ICommonDatasetProps) => {
        const currentTitle = datasetsToCreate.find((dataset) => dataset.id === options.id).title;
        return (
            <InputText
                type="text"
                className="w-full"
                value={currentTitle}
                onChange={(e) => inputChangeHandler(options, e, "title")}
            />
        );
    };

    const dataTypeBodyTemplate = (options:ICommonDatasetProps) => {
        const currentDataType = datasetsToCreate.find((dataset) => dataset.id === options.id).dataType;
        return (
            <Dropdown
                className="w-full"
                options={DATASET_DATA_TYPES}
                value={currentDataType}
                onChange={(e) => inputChangeHandler(options, e, "dataType")}
            />
        );
    };

    const accessTypeBodyTemplate = (options:ICommonDatasetProps) => {
        const currentAccessType = datasetsToCreate.find((dataset) => dataset.id === options.id).accessType;
        return (
            <Dropdown
                className="w-full"
                options={DATASET_ACCESS_TYPES}
                value={currentAccessType}
                onChange={(e) => inputChangeHandler(options, e, "accessType")}
            />
        );
    };

    const unitBodyTemplate = (options:ICommonDatasetProps) => {
        const unitSelectorValue = datasetsToCreate.find((dataset) => dataset.id === options.id).unit;
        return (
            <DatasetUnitSelector
                className="w-full"
                value={unitSelectorValue}
                onChange={(e) => inputChangeHandler(options, e, "unit")}
            />
        );
    };

    const actionBodyTemplate = (options:ICommonDatasetProps) => {
        return (
            <Button
                icon="pi pi-trash"
                className="p-button p-button-danger"
                onClick={() => removeDatasetById(options.id)}
            />
        );
    }

    const inputChangeHandler = (options:ICommonDatasetProps, e: any, field: string) => {
        setDatasetsToCreate((prevState) => {
            const updatedDatasets = [...prevState];
            updatedDatasets.find((dataset) => dataset.id === options.id)[field] = e.target ? e.target.value : e.value;
            return updatedDatasets;
        });
    };

    const removeDatasetById = (id: string) => {
        const updatedDatasets = [...datasetsToCreate];
        const index = updatedDatasets.findIndex((dataset) => dataset.id === id);
        updatedDatasets.splice(index, 1);
        setDatasetsToCreate(updatedDatasets);
    }

    const {mutateAsync} = useCreateCategorizedDataset()

    async function createDatasetsHandler() {
        try {

            const emptyTitleIndex = datasetsToCreate.findIndex((dataset) => dataset.title.trim() === '');

            if (emptyTitleIndex !== -1) {
                easasAlert({ summary: "Validation Error", detail: "Title cannot be empty", severity: "error" });
                return;
            }

            if (datasetsToCreate.length === 0) {
                easasAlert({ summary: "Validation Error", detail: "No datasets to create", severity: "error" });
                return;
            }

            const results = await Promise.allSettled(
                datasetsToCreate.map(async (dataset) => {
                    const data: Partial<BaseDatasetDTO> = {
                        title: dataset.title,
                        categoryId: defaultCategory,
                        dataType: dataset.dataType,
                        accessType: dataset.accessType,
                        unit: dataset.unit,
                    };

                    return mutateAsync(data);
                })
            );

            const successfulMutations = results.filter((result) => result.status === 'fulfilled');
            const failedMutations = results.filter((result) => result.status === 'rejected');


            if (successfulMutations.length === results.length) {
                const createdDatasets = successfulMutations.map((result) => {
                    if (result.status === 'fulfilled') {
                        return result.value
                    }
                });
                setCreatedDatasets(createdDatasets);
                easasAlert({
                  summary: "DATASETS CREATED",
                  detail: `All datasets have been created`,
                  severity: "success",
                });
            } else {
                easasAlert({
                  summary: "CREATION FAILED",
                  detail: `${failedMutations.length} datasets failed to create`,
                  severity: "error",
                });
            }
        } catch (error) {
            console.error('Error creating datasets:', error);
            easasAlert({
              summary: "ERROR",
              detail: "An error occurred while creating datasets",
              severity: "error",
            });
        }
    }

    return (<>
            {createdDatasets.length > 0 ? (<div className="flex col-12 align-items-center">
                    <DataTable value={createdDatasets} className="w-full">
                        <Column
                            field="title"
                            header="Title"
                            body={(e) => (
                                <CopyTextWithButton text={e.title} copyableText={e.title}/>
                            )}
                        />
                        <Column
                            field="identifier"
                            header="Identifier"
                            body={(e) => (
                                <CopyTextWithButton
                                    text={e.identifier}
                                    copyableText={e.identifier}
                                />
                            )}
                        />
                    </DataTable>
                </div>) :
                (<>
                    <DataTable
                        value={datasetsToCreate}
                        header={header}
                    >
                        <Column
                            field="title"
                            header="Title"
                            style={{width: "50%", padding: 0}}
                            body={titleBodyTemplate}/>
                        <Column
                            field="dataType"
                            header="Type"
                            style={{width: "15%", padding: 0}}
                            body={dataTypeBodyTemplate}/>
                        <Column
                            field="accessType"
                            header="Access"
                            style={{width: "15%", padding: 0}}
                            body={accessTypeBodyTemplate}
                        />
                        <Column
                            field="unit"
                            header="Unit"
                            style={{width: "15%", padding: 0}}
                            body={unitBodyTemplate}/>
                        <Column header="Actions" style={{width: "5%", padding: 0, textAlign: "center"}}
                                body={actionBodyTemplate}/>
                    </DataTable>
                    <div className="flex col-12 gap-2">
                        <Button className="p-button-success" label="Create" onClick={createDatasetsHandler}/>
                        <Button className="p-button-danger" label="Cancel"
                                onClick={() => setCreateDialogVisibility(false)}/>
                    </div>
                </>)
            }
        </>
    );
};

export default CreateMultipleDatasets;
